"use client"

import { useRef, useState, useEffect } from "react"
import styles from "./TreatmentTemplate.module.css"
import emailjs from '@emailjs/browser'
import { countryCodes } from "../../utils/countryCodes"

function SuccessModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalCheck}>
          <svg width="72" height="72" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="36" cy="36" r="36" fill="#13A672"/><path d="M23 36l10 10 16-16" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <h2 className={styles.modalTitle}>Thank you!</h2>
        <div className={styles.modalMessage}>
          Your request has been received successfully.<br />
          Our medical consultants will contact you shortly to schedule your free consultation.
        </div>
        <button className={styles.modalButton} onClick={onClose}>Close</button>
      </div>
    </div>
  )
}

export default function TreatmentTemplate({
  title,
  beforeImg,
  afterImg,
  description,
  facts = [], // [{ icon: '⏱️', title: 'Duration', sub: '30–60 Minutes' }, ...]
}) {
  const formRef = useRef(null)
  const [showModal, setShowModal] = useState(false)
  const [isSending, setIsSending] = useState(false)

  // Country code dropdown states (aligned with FixedFormIcon)
  const [selectedCountry, setSelectedCountry] = useState(() => (countryCodes.find(c => c.name === 'United States') || countryCodes[0]))
  const [phone, setPhone] = useState("")
  const [isCodeOpen, setIsCodeOpen] = useState(false)
  const codeWrapRef = useRef(null)

  // Close country code dropdown on outside click
  useEffect(() => {
    if (!isCodeOpen) return
    const onClickOutside = (e) => {
      const el = codeWrapRef.current
      if (el && !el.contains(e.target)) {
        setIsCodeOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    document.addEventListener('touchstart', onClickOutside)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      document.removeEventListener('touchstart', onClickOutside)
    }
  }, [isCodeOpen])

  const sendEmail = (e) => {
    e.preventDefault()
    setIsSending(true)
    const fd = new FormData(formRef.current)
    const params = {
      user_name: fd.get('user_name') || '',
      user_email: fd.get('user_email') || '',
      message: fd.get('message') || '',
      user_phone: fd.get('user_phone') || '',
      country_code: fd.get('country_code') || (selectedCountry?.dial_code || ''),
      user_phone_full: `${fd.get('country_code') || (selectedCountry?.dial_code || '')} ${fd.get('user_phone') || ''}`.trim(),
    }

    emailjs
      .send(
        'service_57xto7k',
        'template_ivxg7n9',
        params,
        { publicKey: 'OPDDEgsIGDGJBTjaz' },
      )
      .then(
        () => {
          setShowModal(true)
          setIsSending(false)
          formRef.current && formRef.current.reset()
          setPhone('')
        },
        (error) => {
          console.error('Email sending failed:', error)
          setIsSending(false)
          alert('There was an error sending your message. Please try again in a short while.')
        },
      )
  }

  return (
    <section className={styles.section}>
      <SuccessModal open={showModal} onClose={() => setShowModal(false)} />
      <div className={styles.container}>
        <h1 className={styles.title}>{title}</h1>

        <div className={styles.topGrid}>
          <div className={styles.collageWrap}>
            <div className={styles.collage}>
              {beforeImg && <img src={beforeImg} alt="Before" className={styles.photoA} />}
              {afterImg && <img src={afterImg} alt="After" className={styles.photoB} />}
            </div>
            {description && <div className={styles.textCard}>{description}</div>}
          </div>

          <div className={styles.rightCol}>
            <div className={styles.factsGrid}>
              {facts.map((f, idx) => (
                <div key={idx} className={styles.factCard}>
                  <div className={styles.factIcon}>{f.icon}</div>
                  <div className={styles.factTitle}>{f.title}</div>
                  <div className={styles.factSub}>{f.sub}</div>
                </div>
              ))}
            </div>

            <div className={styles.callBlock}>
              <div className={styles.callTitle}>YOUR COMPLIMENTARY CONSULTATION</div>
              <form ref={formRef} className={styles.callRow} onSubmit={sendEmail}>
                <div className={styles.phoneWrapper}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 2.8fr', gap: 0 }}>
                    <div className={styles.codeSelectWrap} ref={codeWrapRef}>
                    <button
                      type="button"
                      className={styles.codeSelectTrigger}
                      onClick={() => setIsCodeOpen(o => !o)}
                      aria-haspopup="listbox"
                      aria-expanded={isCodeOpen}
                    >
                      <span className={styles.codeLabel}>{selectedCountry?.name || 'United States'}</span>
                      <span className={styles.codeValue}>{selectedCountry?.dial_code || '+1'}</span>
                    </button>
                    {isCodeOpen && (
                      <ul className={styles.codeList} role="listbox">
                        {(() => {
                          const pinnedNames = ['United States','Russian Federation','United Kingdom','Turkey']
                          const pinned = countryCodes.filter(c => pinnedNames.includes(c.name))
                          const rest = countryCodes
                            .filter(c => !pinnedNames.includes(c.name))
                            .sort((a, b) => a.name.localeCompare(b.name))
                          return (
                            <>
                              {pinned.map((c) => (
                                <li
                                  key={'pinned-' + c.code + c.dial_code}
                                  role="option"
                                  className={styles.codeItem}
                                  onClick={() => { setSelectedCountry(c); setIsCodeOpen(false) }}
                                >
                                  <span className={styles.codeLabel}>{c.name}</span>
                                  <span className={styles.codeValue}>{c.dial_code}</span>
                                </li>
                              ))}
                              <li className={styles.codeDivider} aria-hidden="true" />
                              {rest.map((c) => (
                                <li
                                  key={c.code + c.dial_code}
                                  role="option"
                                  className={styles.codeItem}
                                  onClick={() => { setSelectedCountry(c); setIsCodeOpen(false) }}
                                >
                                  <span className={styles.codeLabel}>{c.name}</span>
                                  <span className={styles.codeValue}>{c.dial_code}</span>
                                </li>
                              ))}
                            </>
                          )
                        })()}
                      </ul>
                    )}
                    <input type="hidden" name="country_code" value={selectedCountry?.dial_code || ''} />
                  </div>
                  <input
                    type="tel"
                    placeholder="YOUR PHONE NUMBER "
                    name="user_phone"
                    required
                    className={styles.input}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                </div>
                <input type="hidden" name="user_phone_full" value={`${selectedCountry?.dial_code || ''} ${phone}`} />
                <button type="submit" className={styles.ctaBtn} disabled={isSending}>
                  {isSending ? 'Sending...' : 'GET FREE CONSULTATION'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}


